import React, { useState } from "react";
import { Box, Popover } from "@mui/material";
import { useTranslation } from "react-i18next";

import { IconChevronDown } from "../../design-system/icons";

export const SETTINGS_COLOR_PALETTE = Object.freeze([
  "#4CCEAC",
  "#0E7490",
  "#2563EB",
  "#64748B",
  "#7C3AED",
  "#DB2777",
  "#DC2626",
  "#EA580C",
  "#D97706",
  "#16A34A",
]);

const HEX_COLOR_PATTERN = /^#[0-9a-fA-F]{6}$/;

const normalizeHexColor = (value, fallback) => {
  const color = String(value || "").trim();
  return HEX_COLOR_PATTERN.test(color) ? color.toUpperCase() : fallback;
};

export default function SettingsColorPicker({
  value,
  label,
  onChange,
  disabled = false,
  palette = SETTINGS_COLOR_PALETTE,
}) {
  const { t } = useTranslation();
  const colors = Array.isArray(palette) && palette.length ? palette : SETTINGS_COLOR_PALETTE;
  const fallbackColor = normalizeHexColor(colors[0], SETTINGS_COLOR_PALETTE[0]);
  const resolvedLabel = label || t("settings_ui.selection");
  const [anchorEl, setAnchorEl] = useState(null);
  const selectedColor = normalizeHexColor(value, fallbackColor);
  const [customColor, setCustomColor] = useState(selectedColor);
  const normalizedCustomColor = normalizeHexColor(customColor, selectedColor);
  const open = Boolean(anchorEl);
  const customColorChanged = normalizedCustomColor !== selectedColor;

  const applyColor = (color) => {
    onChange(normalizeHexColor(color, selectedColor));
    setAnchorEl(null);
  };

  const openPopover = (event) => {
    setCustomColor(selectedColor);
    setAnchorEl(event.currentTarget);
  };

  return (
    <>
      <button
        type="button"
        className={`spv2-color-swatch${open ? " is-open" : ""}`}
        aria-label={t("settings_ui.change_color_for", { label: resolvedLabel })}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
        onClick={openPopover}
      >
        <span
          className="spv2-color-swatch-dot"
          style={{ "--spv2-color-editor-color": selectedColor }}
          aria-hidden="true"
        />
        <IconChevronDown className="spv2-color-swatch-caret" />
      </button>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ zIndex: 3400 }}
        PaperProps={{ className: "spv2-color-popover-paper" }}
      >
        <Box className="spv2-color-popover">
          <Box
            className="spv2-color-popover-grid"
            role="listbox"
            aria-label={t("settings_ui.color_for", { label: resolvedLabel })}
          >
            {colors.map((color) => {
              const normalizedColor = normalizeHexColor(color, fallbackColor);
              return (
                <button
                  key={normalizedColor}
                  type="button"
                  role="option"
                  aria-selected={selectedColor === normalizedColor}
                  className={`spv2-color-popover-preset${selectedColor === normalizedColor ? " is-active" : ""}`}
                  style={{ "--spv2-color-editor-color": normalizedColor }}
                  aria-label={t("settings_ui.set_color_for", { label: resolvedLabel, color: normalizedColor })}
                  onClick={() => applyColor(normalizedColor)}
                />
              );
            })}
          </Box>

          <Box className="spv2-color-popover-custom-section">
            <label className="spv2-color-popover-custom">
              <span
                className="spv2-color-popover-custom-dot"
                style={{ "--spv2-color-editor-color": normalizedCustomColor }}
                aria-hidden="true"
              />
              <span className="spv2-color-popover-custom-label">{t("settings_ui.custom_color")}</span>
              <span className="spv2-color-popover-custom-value">{normalizedCustomColor}</span>
              <input
                type="color"
                value={normalizedCustomColor}
                aria-label={t("settings_ui.choose_color_for", { label: resolvedLabel })}
                onChange={(event) => setCustomColor(event.target.value)}
              />
            </label>
            <button
              type="button"
              className="spv2-color-popover-apply"
              disabled={!customColorChanged}
              onClick={() => applyColor(normalizedCustomColor)}
            >
              {t("settings_ui.apply")}
            </button>
          </Box>
        </Box>
      </Popover>
    </>
  );
}
